import { type FormEvent, useState } from 'react';
import { api, type TwoFactorStatus } from '../api';
import { useLoad } from '../hooks';
import { CopyButton, Dialog, ErrorNote, formatDate, Status, useToast } from './ui';

type Setup = { secret: string; otpauthUrl: string; qrSvg: string };

/** Settings → Two-factor authentication: set up an authenticator app, recovery codes, turn off. */
export function TwoFactorCard() {
  const { data, error, reload } = useLoad(() => api<TwoFactorStatus>('GET', '/auth/2fa'), []);
  const [dialog, setDialog] = useState<'setup' | 'disable' | 'codes' | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const toast = useToast();

  return (
    <div className="card">
      <div className="card-head">
        <div>
          <h2>Two-factor authentication</h2>
          <p>
            After the email code, also ask for a code from an authenticator app such as Google
            Authenticator, 1Password or Authy.
            {data?.required ? ' Required for admins.' : ''}
          </p>
        </div>
        {data && !data.enabled ? (
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={() => setDialog('setup')}
          >
            Turn on
          </button>
        ) : null}
      </div>
      <ErrorNote error={error} />
      {data ? (
        <div className="card-body">
          <dl className="facts" style={{ gridTemplateColumns: '140px minmax(0, 1fr)' }}>
            <dt>Status</dt>
            <dd>
              {data.enabled ? (
                <Status status={`On since ${formatDate(data.enabledAt)}`} tone="ok" />
              ) : (
                <Status status="Off" tone={data.required ? 'warn' : ''} />
              )}
            </dd>
            {data.enabled ? (
              <>
                <dt>Recovery codes</dt>
                <dd>
                  {data.recoveryCodesLeft} of 10 left ·{' '}
                  <button type="button" className="link-btn" onClick={() => setDialog('codes')}>
                    Make new codes
                  </button>
                  {data.required ? null : (
                    <>
                      {' · '}
                      <button
                        type="button"
                        className="link-btn"
                        onClick={() => setDialog('disable')}
                      >
                        Turn off
                      </button>
                    </>
                  )}
                </dd>
              </>
            ) : null}
          </dl>
        </div>
      ) : null}

      {dialog === 'setup' ? (
        <SetupDialog
          onClose={() => setDialog(null)}
          onEnabled={(codes) => {
            setDialog(null);
            setRecoveryCodes(codes);
            reload();
          }}
        />
      ) : null}
      {dialog === 'disable' ? (
        <CodeDialog
          title="Turn off two-factor authentication?"
          description="Logging in will only need the email code again. Enter a code from your app, or a recovery code."
          confirm="Turn off"
          danger
          onClose={() => setDialog(null)}
          onSubmit={async (code) => {
            await api('POST', '/auth/2fa/disable', { code });
            setDialog(null);
            toast.show('Two-factor authentication is off');
            reload();
          }}
        />
      ) : null}
      {dialog === 'codes' ? (
        <CodeDialog
          title="Make new recovery codes?"
          description="Your current recovery codes stop working. Enter a code from your app to continue."
          confirm="Make new codes"
          onClose={() => setDialog(null)}
          onSubmit={async (code) => {
            const res = await api<{ recoveryCodes: string[] }>('POST', '/auth/2fa/recovery-codes', {
              code,
            });
            setDialog(null);
            setRecoveryCodes(res.recoveryCodes);
            reload();
          }}
        />
      ) : null}
      {recoveryCodes ? (
        <RecoveryCodesDialog codes={recoveryCodes} onClose={() => setRecoveryCodes(null)} />
      ) : null}
      {toast.node}
    </div>
  );
}

function SetupDialog({
  onClose,
  onEnabled,
}: {
  onClose: () => void;
  onEnabled: (recoveryCodes: string[]) => void;
}) {
  const setup = useLoad(() => api<Setup>('POST', '/auth/2fa/setup'), []);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api<{ recoveryCodes: string[] }>('POST', '/auth/2fa/enable', { code });
      onEnabled(res.recoveryCodes);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Set up two-factor authentication"
      description="Scan the QR code with your authenticator app, then enter the 6-digit code it shows."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="totp-setup"
            className="btn btn-primary"
            disabled={busy || code.length !== 6 || !setup.data}
          >
            {busy ? 'Checking…' : 'Turn on'}
          </button>
        </>
      }
    >
      <ErrorNote error={setup.error} />
      {setup.data ? (
        <form id="totp-setup" onSubmit={submit} style={{ display: 'contents' }}>
          <img
            className="totp-qr"
            src={`data:image/svg+xml;utf8,${encodeURIComponent(setup.data.qrSvg)}`}
            alt="QR code for your authenticator app"
            width={176}
            height={176}
          />
          <div className="field">
            <div className="label">Can't scan it? Enter this key in the app</div>
            <div className="secret-box">
              <code>{setup.data.secret.replace(/(.{4})/g, '$1 ').trim()}</code>
              <CopyButton value={setup.data.secret} label="Copy key" />
            </div>
          </div>
          <div className="field">
            <label htmlFor="totp-code">6-digit code from the app</label>
            <input
              id="totp-code"
              className="input code-input"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <ErrorNote error={error} />
        </form>
      ) : null}
    </Dialog>
  );
}

/** Ask for a current code (authenticator or recovery) before a sensitive change. */
function CodeDialog({
  title,
  description,
  confirm,
  danger,
  onClose,
  onSubmit,
}: {
  title: string;
  description: string;
  confirm: string;
  danger?: boolean;
  onClose: () => void;
  onSubmit: (code: string) => Promise<void>;
}) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  async function submit(e?: FormEvent) {
    e?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await onSubmit(code);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={title}
      description={description}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            form="totp-confirm"
            className={`btn ${danger ? 'btn-danger' : 'btn-primary'}`}
            disabled={busy || code.length < 6}
          >
            {busy ? 'Checking…' : confirm}
          </button>
        </>
      }
    >
      <form id="totp-confirm" onSubmit={submit} style={{ display: 'contents' }}>
        <div className="field">
          <label htmlFor="totp-confirm-code">Authentication code</label>
          <input
            id="totp-confirm-code"
            className="input code-input"
            autoComplete="one-time-code"
            autoCapitalize="none"
            spellCheck={false}
            maxLength={11}
            value={code}
            onChange={(e) => setCode(e.target.value.trim())}
          />
        </div>
        <ErrorNote error={error} />
      </form>
    </Dialog>
  );
}

function RecoveryCodesDialog({ codes, onClose }: { codes: string[]; onClose: () => void }) {
  const text = codes.join('\n');
  function download() {
    const url = URL.createObjectURL(new Blob([`${text}\n`], { type: 'text/plain' }));
    const a = Object.assign(document.createElement('a'), {
      href: url,
      download: 'vaartix-recovery-codes.txt',
    });
    a.click();
    URL.revokeObjectURL(url);
  }
  return (
    <Dialog
      title="Save your recovery codes"
      description="If you lose your phone, each code logs you in once instead of an app code. They're shown only now: keep them in your password manager."
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={download}>
            Download
          </button>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            I've saved them
          </button>
        </>
      }
    >
      <div className="recovery-codes">
        {codes.map((c) => (
          <code key={c}>{c}</code>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <CopyButton value={text} label="Copy codes" />
        <span className="muted" style={{ fontSize: 13 }}>
          Copy all codes
        </span>
      </div>
    </Dialog>
  );
}
